/**
 * <preorder-limit-guard data-limit="N">
 *
 * Enforces the per-customer pre-order limit (N) on the client, hardened against the
 * rapid-click race where the cart's quantity hasn't settled yet:
 *
 *   - Authoritative cart count from /cart.js (seeded synchronously from the quantity
 *     input's data-cart-quantity on connect, then reconciled on every cart:update).
 *   - A `pending` counter incremented the moment a valid add is clicked, so several
 *     fast clicks before the cart settles are still counted.
 *   - A capture-phase click interceptor on .add-to-cart-button that BLOCKS the add
 *     (preventDefault + stopImmediatePropagation), grays the button, and shows the
 *     limit message once cartQty + pending + thisAdd would exceed the limit.
 *
 * This stops normal/rapid UI over-ordering. It is still client-side (Tier 1): a
 * hand-crafted /cart/add.js call can bypass it — only a Shopify Cart/Checkout
 * Validation Function (Tier 2, a custom app) guarantees it server-side.
 *
 * Rendered by blocks/buy-buttons-custom.liquid for active pre-orders that set
 * custom.preorder_limit_per_customer. See features/preorder-coming-soon.md.
 */
if (!customElements.get('preorder-limit-guard')) {
  customElements.define(
    'preorder-limit-guard',
    class PreorderLimitGuard extends HTMLElement {
      connectedCallback() {
        this.limit = parseInt(this.getAttribute('data-limit'), 10) || 0;
        this.scope = this.closest('product-form-component') || this.closest('form') || document;
        this.pending = 0;
        this.controller = new AbortController();
        const { signal } = this.controller;

        // Seed synchronously from the server-rendered cart count, then get the truth.
        const qInput = this.scope.querySelector('input[data-cart-quantity]');
        this.cartQty = qInput ? parseInt(qInput.getAttribute('data-cart-quantity'), 10) || 0 : 0;
        this.render();

        this.onClick = this.onClick.bind(this);
        this.onCartChange = this.onCartChange.bind(this);
        this.scope.addEventListener('click', this.onClick, { capture: true, signal });
        document.addEventListener('cart:update', this.onCartChange, { signal });
        document.addEventListener('variant:update', this.onCartChange, { signal });

        this.refresh();
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      get button() {
        return this.scope.querySelector('.add-to-cart-button');
      }

      variantId() {
        const el = this.scope.querySelector('input[name="id"]');
        return el ? el.value : null;
      }

      addValue() {
        const q = this.scope.querySelector('input[name="quantity"]');
        const v = q ? parseInt(q.value, 10) : 1;
        return v > 0 ? v : 1;
      }

      // Capture clicks on the add button BEFORE the form submits, so rapid clicks are
      // counted synchronously and blocked once the projected total exceeds the limit.
      onClick(event) {
        if (!this.limit) return;
        const target = event.target;
        const button = target && target.closest ? target.closest('.add-to-cart-button') : null;
        if (!button || !this.scope.contains(button)) return;

        const value = this.addValue();
        if (this.cartQty + this.pending + value > this.limit) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showMessage();
          this.setGray(true);
          return;
        }
        this.pending += value;
        if (this.cartQty + this.pending >= this.limit) this.setGray(true);
      }

      onCartChange() {
        this.pending = 0;
        this.refresh();
      }

      refresh() {
        if (!this.limit) return;
        const variantId = this.variantId();
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((cart) => {
            let qty = 0;
            (cart.items || []).forEach((item) => {
              if (String(item.variant_id) === String(variantId)) qty += item.quantity;
            });
            this.cartQty = qty;
            this.render();
          })
          .catch(() => {});
      }

      render() {
        this.setGray(this.cartQty + this.pending >= this.limit);
      }

      setGray(on) {
        const button = this.button;
        if (!button) return;
        button.classList.toggle('preorder-limit-reached', on);
        if (on) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }

      showMessage() {
        const errorEl = this.scope.querySelector('.product-form-text__error');
        if (!errorEl) return;
        errorEl.classList.remove('hidden');
        let textEl = errorEl.querySelector('.preorder-limit-msg');
        if (!textEl) {
          textEl = document.createElement('span');
          textEl.className = 'preorder-limit-msg';
          errorEl.appendChild(textEl);
        }
        textEl.textContent = ' Limit ' + this.limit + ' per customer on this pre-order';
        if (this.messageTimeout) clearTimeout(this.messageTimeout);
        this.messageTimeout = setTimeout(() => errorEl.classList.add('hidden'), 5000);
      }
    }
  );
}
